import React from "react";
import { Box } from "@mui/material";
import { styled } from "@mui/material/styles";
import { WarningRounded } from "@mui/icons-material";
import { LOCATION_STATUS_STYLES } from "../../../types/LocationStatus";
import { useLocationCardContext } from "./LocationCardContext";
import ImageComponent from "../../skeleton/ImageComponent";
import { constructListingImageUrl } from "../../../util/ConstructImageUrlUtil";

interface LocationCardImageProps {
  /** `listingImageFileName` from `ILocationData`. Pass `undefined` to show the placeholder. */
  fileName?: string | null;
  /** Accessible alt text for the image. Defaults to "Location image". */
  alt?: string;
}

const ImageRoot = styled(Box)(({ theme }) => ({
  gridArea: "image",
  background: "#d4dde6",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  position: "relative",
  overflow: "hidden",
  // xs: full-bleed banner across the top of the card (cancels the content
  // padding), a fixed 160px tall — about the tablet photo's height — so it
  // never dominates a phone screen. A 4:3 photo shows its middle ~55%.
  margin: "-16px -16px 0",
  width: "calc(100% + 32px)",
  height: 160,
  // SM+: fixed-size 4:3 thumbnail beside the header; a taller card grows the
  // content, never the photo.
  [theme.breakpoints.up("sm")]: {
    margin: 0,
    width: 224,
    height: "auto",
    aspectRatio: "4 / 3",
    borderRadius: 6,
    alignSelf: "start",
  },
  // LG+: a 320 × 240 column beside the whole card; about the height of the
  // header plus the field groups, so it fills the card without a gap.
  [theme.breakpoints.up("lg")]: {
    width: 320,
  },
}));

const StyledImage = styled(ImageComponent)({
  width: "100%",
  height: "100%",
  objectFit: "cover",
  position: "absolute",
  top: 0,
  left: 0,
});

const PlaceholderLabel = styled(Box)({
  fontSize: "0.6429rem", // 9px at 14px root
  letterSpacing: "0.5px",
  color: "#8a9aaa",
  textAlign: "center",
  textTransform: "uppercase",
  padding: "4px",
  lineHeight: 1.4,
  marginTop: 6,
});

const StatusRibbon = styled(Box, {
  shouldForwardProp: (prop) => prop !== "ownerBg",
})<{ ownerBg: string }>( ({ ownerBg }) => ({
  position: "absolute",
  top: 10,
  left: 0,
  padding: "3px 10px 3px 8px",
  fontSize: "0.7143rem", // 10px at 14px root
  fontWeight: 700,
  letterSpacing: "0.5px",
  borderRadius: "0 4px 4px 0",
  color: "#fff",
  textTransform: "uppercase",
  background: ownerBg,
  zIndex: 1,
}));

const STATUS_RIBBON_LABEL: Record<string, string> = {
  draft: "Draft",
  pending: "Pending",
  active: "Active",
  deactivated: "Deactivated",
};

/**
 * Photo for a `LocationCard` (place inside `LocationCard.Content`).
 *
 * - Shows a status ribbon in the top-left corner colored via `LOCATION_STATUS_STYLES`.
 * - Falls back to a placeholder icon when no `fileName` is provided.
 * - Phones: a full-width banner, 160px tall. SM+: a 4:3
 *   thumbnail beside the header, 224px wide; from LG a 320px 4:3 photo
 *   column beside the whole card.
 * - Reads `derivedStatus` from the nearest `LocationCard` context (no prop needed).
 */
const LocationCardImage = ({ fileName, alt = "Location image" }: LocationCardImageProps) => {
  const { derivedStatus } = useLocationCardContext();
  const tokens = LOCATION_STATUS_STYLES[derivedStatus];

  return (
    <ImageRoot>
      {fileName ? (
        <StyledImage
          src={constructListingImageUrl(fileName)}
          alt={alt}
        />
      ) : (
        <>
          <WarningRounded sx={{ fontSize: "2rem", color: "#aabbc8" }} aria-hidden="true" />
          <PlaceholderLabel>No Image<br />Available</PlaceholderLabel>
        </>
      )}
      <StatusRibbon ownerBg={tokens.color} aria-label={`Status: ${STATUS_RIBBON_LABEL[derivedStatus]}`}>
        {STATUS_RIBBON_LABEL[derivedStatus]}
      </StatusRibbon>
    </ImageRoot>
  );
};

export default LocationCardImage;

